/**
 * The numbers and the pure rules behind the answers layout, kept apart from the components so
 * they can be tested without rendering.
 */

import { format, isValid } from 'date-fns';

/**
 * The classes every card in the answers draws with, the question's and each answer's, so the view
 * keeps one gray and one line: a white card on an `outline-200` border, a `background-50` header
 * strip, and `outline-200` for the line under the strip. The strip is 52px, a 48px row and 2px
 * above and below it.
 */
export const ANSWERS_CARD_CLASS =
  'overflow-hidden rounded-md border border-outline-200 bg-background-0';

export const ANSWERS_STRIP_CLASS = 'bg-background-50 px-4 py-0.5';

export const ANSWERS_STRIP_RULE_CLASS = 'border-b border-outline-200';

/**
 * The post date's color. In the answers view it is `typography-600`, which clears 4.5:1 on the
 * page. Flag-off it keeps main's `typography-500`, so a post without answers reads as it always has.
 */
export const postDateClassName = (showsAnswers: boolean) =>
  showsAnswers ? 'text-typography-600' : 'text-typography-500';

export const specialistName = (position: number) => `Primum Specialist ${position}`;

/** The DOM id of an answer's card. Web only. */
export const answerAnchor = (position: number) => `inquiry-library-answer-${position}`;

/** The DOM id of an answer's body, which its header button controls. Web only. */
export const answerBodyId = (position: number) => `inquiry-library-answer-body-${position}`;

/** The key an answer's collapsed state is held under. The row id, never a position that can move. */
export const answerKey = (answerId: number) => `answer-${answerId}`;

/** The DOM id of an answer's heading. Web only. */
export const answerHeadingId = (position: number) => `inquiry-library-answer-heading-${position}`;

/**
 * The count of an answer's follow-ups, "1 follow-up reply" or "N follow-up replies", spoken as the
 * start of the group's name and never drawn. The count is the replies after the answer itself, so
 * the answer, an answer's first reply, is never in it.
 */
export const followUpLabel = (count: number) =>
  count === 1 ? '1 follow-up reply' : `${count} follow-up replies`;

/**
 * The day a reply was sent, in the reader's local time, or null when it should show none. Only the
 * day: it tells a reader how current an answer is, while the time of day adds nothing for them and
 * pins an anonymous specialist to a moment. Null for a reply with no `sentAt`, and for one that
 * does not parse, so a bad value draws nothing rather than "Invalid Date".
 */
export function replyDate(sentAt: string | null | undefined): string | null {
  if (!sentAt) {
    return null;
  }

  const date = new Date(sentAt);

  return isValid(date) ? format(date, 'MMM d, yyyy') : null;
}

/** The name of the follow-up group, which says whose answer it follows. */
export const followUpGroupName = (position: number, count: number) =>
  `${followUpLabel(count)} to ${specialistName(position)}`;
